import { useEffect, useMemo, useState } from "react";
import {
  Wrench, CurrencyDollar, Truck, ChartBar, Calendar, ArrowsClockwise,
  WarningCircle, CheckCircle,
} from "@phosphor-icons/react";
import { maintenanceApi } from "@/lib/api";

const SERVICE_LABEL = {
  oil_change: "Oil Change",
  tire_rotation: "Tire Rotation",
  brake_inspection: "Brake Inspection",
  tire_replacement: "Tire Replacement",
  engine_repair: "Engine Repair",
  transmission: "Transmission",
  dot_inspection: "DOT Inspection",
  other: "Other",
};

export default function MaintenancePage() {
  const [summary, setSummary] = useState(null);
  const [due, setDue] = useState(null);
  const [days, setDays] = useState(365);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        maintenanceApi.summary(days),
        maintenanceApi.due(),
      ]);
      setSummary(s);
      setDue(d);
    } catch (e) {
      console.warn("Maintenance dashboard load failed", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [days]); // eslint-disable-line

  const maxMonthCost = useMemo(() => {
    return Math.max(1, ...((summary?.by_month || []).map((m) => m.cost_usd)));
  }, [summary]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="maintenance-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">Fleet Health</div>
          <h1 className="font-heading font-black text-3xl tracking-tight flex items-center gap-2">
            <Wrench size={22} className="text-[#ff8c00]" weight="bold" />
            Maintenance Costs
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none px-3 py-2 text-xs uppercase tracking-[0.2em] font-bold"
            data-testid="maintenance-window-select"
          >
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last 12 months</option>
            <option value={730}>Last 24 months</option>
          </select>
          <button
            onClick={load}
            className="w-9 h-9 border border-[#2a2a2a] hover:border-[#ff8c00] hover:text-[#ff8c00] flex items-center justify-center"
            data-testid="maintenance-refresh"
            title="Refresh"
          >
            <ArrowsClockwise size={12} weight="bold" />
          </button>
        </div>
      </div>

      {loading && <div className="text-center py-12 text-xs uppercase tracking-[0.2em] text-[#71717a]">Crunching numbers…</div>}

      {!loading && summary && (
        <>
          {/* Top stat row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat
              icon={<CurrencyDollar size={14} className="text-[#22c55e]" weight="bold" />}
              label="Total Spend"
              value={`$${summary.total_cost_usd.toLocaleString()}`}
              accent="#22c55e"
              testId="stat-total"
            />
            <Stat
              icon={<ChartBar size={14} className="text-[#4682b4]" weight="bold" />}
              label="Service Events"
              value={summary.total_events}
              accent="#4682b4"
              testId="stat-events"
            />
            <Stat
              icon={<Wrench size={14} className="text-[#ff8c00]" weight="bold" />}
              label="Avg / Event"
              value={`$${summary.avg_cost_per_event.toLocaleString()}`}
              accent="#ff8c00"
              testId="stat-avg"
            />
            <Stat
              icon={(due?.overdue_count || 0) > 0
                ? <WarningCircle size={14} className="text-[#ef4444]" weight="fill" />
                : <CheckCircle size={14} className="text-[#22c55e]" weight="fill" />
              }
              label="Overdue / Due Soon"
              value={`${due?.overdue_count ?? 0} / ${due?.due_soon_count ?? 0}`}
              accent={(due?.overdue_count || 0) > 0 ? "#ef4444" : "#22c55e"}
              testId="stat-due"
            />
          </div>

          {/* Monthly trend */}
          {summary.by_month.length > 0 && (
            <div className="bg-[#0d0d0d] border border-[#2a2a2a] mb-6" data-testid="monthly-trend">
              <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center gap-2">
                <Calendar size={14} className="text-[#4682b4]" weight="bold" />
                <h2 className="font-heading font-black text-sm tracking-tight uppercase">Monthly Spend</h2>
              </div>
              <div className="p-4 flex items-end gap-2 h-44" data-testid="monthly-bars">
                {summary.by_month.map((m) => {
                  const h = Math.max(4, Math.round((m.cost_usd / maxMonthCost) * 140));
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-1" title={`${m.month}: $${m.cost_usd.toLocaleString()} (${m.events} events)`} data-testid={`monthly-bar-${m.month}`}>
                      <div className="text-[9px] font-mono text-[#a1a1aa]">${m.cost_usd.toFixed(0)}</div>
                      <div className="w-full bg-[#ff8c00]" style={{ height: `${h}px` }} />
                      <div className="text-[9px] font-mono text-[#71717a]">{m.month.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* By type */}
            <div className="bg-[#0d0d0d] border border-[#2a2a2a]" data-testid="by-type-card">
              <div className="px-4 py-3 border-b border-[#2a2a2a]">
                <h2 className="font-heading font-black text-sm tracking-tight uppercase">By Service Type</h2>
              </div>
              <div>
                {summary.by_type.length === 0 && <div className="px-4 py-6 text-xs uppercase tracking-[0.2em] text-[#71717a] text-center">No events in window</div>}
                {summary.by_type.map((t) => (
                  <div key={t.type} className="px-4 py-2.5 border-b border-[#2a2a2a] last:border-b-0 grid grid-cols-12 gap-2 items-center text-xs" data-testid={`by-type-${t.type}`}>
                    <div className="col-span-6 font-bold">{SERVICE_LABEL[t.type] || t.type}</div>
                    <div className="col-span-3 font-mono text-[#a1a1aa] text-[10px] uppercase tracking-[0.2em]">{t.events} event{t.events !== 1 ? "s" : ""}</div>
                    <div className="col-span-3 font-mono text-right text-[#22c55e] font-bold">${t.cost_usd.toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* By asset */}
            <div className="bg-[#0d0d0d] border border-[#2a2a2a]" data-testid="by-asset-card">
              <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center justify-between">
                <h2 className="font-heading font-black text-sm tracking-tight uppercase">Spend by Asset</h2>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717a]">Top 10</span>
              </div>
              <div>
                {summary.by_asset.length === 0 && <div className="px-4 py-6 text-xs uppercase tracking-[0.2em] text-[#71717a] text-center">No events in window</div>}
                {summary.by_asset.slice(0, 10).map((a) => (
                  <div key={a.asset_id} className="px-4 py-2.5 border-b border-[#2a2a2a] last:border-b-0 grid grid-cols-12 gap-2 items-center text-xs" data-testid={`by-asset-${a.asset_id}`}>
                    <div className="col-span-5 font-mono font-bold flex items-center gap-2">
                      <Truck size={11} className="text-[#4682b4]" /> {a.asset_name}
                    </div>
                    <div className="col-span-3 text-[10px] uppercase tracking-[0.2em] text-[#71717a]">{a.events}× · {a.hours_meter || 0}h</div>
                    <div className="col-span-2 text-[10px] font-mono text-[#a1a1aa] text-right">{a.cost_per_hour != null ? `$${a.cost_per_hour}/h` : "—"}</div>
                    <div className="col-span-2 font-mono text-right text-[#22c55e] font-bold">${a.cost_usd.toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Due assets table */}
          {due && due.rows.length > 0 && (
            <div className="bg-[#0d0d0d] border border-[#ef4444]/40" data-testid="due-table">
              <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center gap-2">
                <WarningCircle size={14} className="text-[#ef4444]" weight="fill" />
                <h2 className="font-heading font-black text-sm tracking-tight uppercase">Service Required</h2>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#1a1a1a]">
                    {["Asset", "Status", "Hours", "Interval", "Last Service", "Hours To Service"].map((h) => (
                      <th key={h} className="text-left px-4 py-2 text-[9px] uppercase tracking-[0.2em] text-[#71717a] font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {due.rows.map((r) => (
                    <tr key={r.asset_id} className="border-t border-[#2a2a2a]" data-testid={`due-row-${r.asset_id}`}>
                      <td className="px-4 py-2 font-mono font-bold">{r.asset_name}</td>
                      <td className="px-4 py-2">
                        <span
                          className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] font-bold border"
                          style={{
                            color: r.status === "overdue" ? "#ef4444" : "#ff8c00",
                            borderColor: r.status === "overdue" ? "#ef4444" : "#ff8c00",
                          }}
                        >
                          {r.status.replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono">{r.hours_meter}</td>
                      <td className="px-4 py-2 font-mono">{r.service_interval_hours}</td>
                      <td className="px-4 py-2 font-mono text-[10px]">{r.last_serviced_at ? new Date(r.last_serviced_at).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-2 font-mono font-bold" style={{ color: r.hours_to_service <= 0 ? "#ef4444" : "#ff8c00" }}>
                        {r.hours_to_service > 0 ? `${r.hours_to_service}h` : `${-r.hours_to_service}h overdue`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ icon, label, value, accent, testId }) {
  return (
    <div className="bg-[#0d0d0d] border border-[#2a2a2a] p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <div className="text-[9px] uppercase tracking-[0.25em] text-[#71717a] font-bold">{label}</div>
      </div>
      <div className="font-heading font-black text-2xl tracking-tight" style={{ color: accent }}>{value}</div>
    </div>
  );
}
